import { useRef } from 'preact/hooks';

import { useSchibstedAccount } from '../../../utils/SchibstedAccountProvider';

import { useProvider } from './useProvider';
import { useCurrentAsset } from './CurrentAssetProvider';
import { Geolocation, useGeolocation } from './GeolocationProvider';
import { ResolvedExperiment, useExperiments } from './ExperimentsProvider';
import { useConfig } from './ConfigProvider';

const getGeolocationTag = (location: Geolocation) => {
  if (
    typeof location.eea === 'undefined' &&
    typeof location.local === 'undefined'
  ) {
    return 'unknown';
  }

  if (location.eea) {
    return 'eea';
  }

  if (location.local) {
    return 'local';
  }

  return 'non-eea';
};

const getExperimentsTags = (experiments: ResolvedExperiment[]) => {
  return Object.values(experiments).reduce((obj, experiment) => {
    return { ...obj, [`experiment:${experiment.id}`]: experiment.variant };
  }, {});
};

export const useReportContext = () => {
  const { configProvider } = useProvider();
  const { getUserIdAsync, getUserAccesses } = useSchibstedAccount();
  const { asset } = useCurrentAsset();
  const { location } = useGeolocation();
  const experiments = useExperiments();
  const { experiments: externalExperiments } = useConfig();

  const getDefaultContext = useRef<() => Promise<Record<string, any>>>(null!);

  getDefaultContext.current = async (): Promise<Record<string, any>> => {
    const [userId, accesses] = await Promise.all([
      getUserIdAsync(),
      getUserAccesses(),
    ]);
    const experimentsTags = getExperimentsTags([
      ...Object.values(experiments as Record<string, ResolvedExperiment>),
      ...externalExperiments,
    ]);

    return {
      user: {
        id: userId || undefined,
        access: accesses.join(',') || undefined,
        geolocation: getGeolocationTag(location),
      },
      tags: {
        provider: configProvider,
        url: window.location.href,
        ...experimentsTags,
        ...(asset && {
          'content.id': asset.id,
          'content.provider': asset.provider,
          'content.streamType': asset.streamType,
          'content.properties':
            asset.streamConfiguration.properties.join(',') || undefined,
        }),
      },
    };
  };

  return {
    getDefaultContext,
  };
};
